/**
 * Runtime configuration, read once from Vite env (`.env`, `.env.local`).
 * See `.env.example` for every key.
 */
export type DataSourceKind = "platform" | "mock";

export interface AppConfig {
  /** Base URL of the Sky Logics platform API, e.g. http://localhost:8000 */
  apiUrl: string | undefined;
  /** API key issued to a service principal (scripts/issue_key.py). */
  apiKey: string | undefined;
  /** Development auth headers (platform started with SLT_DEV_AUTH=1); never set in production. */
  devTenant: string | undefined;
  devPrincipal: string | undefined;
  /** Which data source to use. "auto" picks platform when apiUrl is set. */
  dataSource: DataSourceKind | "auto";
  /** Fall back to mock data if the platform is unreachable at start-up. */
  mockFallback: boolean;
  /** Default site code to open when the bootstrap does not decide. */
  defaultSiteCode: string;
  /**
   * The identity provider people sign in with, when this build has one.
   *
   * Set, it replaces the in-browser gate: a person signs in with Google
   * Identity Platform and every request carries their token, so the platform
   * knows who is asking. These three values are the provider's public
   * identifiers, not credentials — the web API key names the project and is
   * restricted to this site's address; it authorises nothing on the platform.
   */
  identity: IdentityConfig | undefined;
}

export interface IdentityConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
}

function flag(v: string | undefined, dflt: boolean): boolean {
  if (v == null || v === "") return dflt;
  return v === "1" || v.toLowerCase() === "true";
}

const env = import.meta.env;

/** What the build was given. Settings can override it, below. */
export const builtConfig: AppConfig = {
  apiUrl: env.VITE_SLT_API_URL || undefined,
  apiKey: env.VITE_SLT_API_KEY || undefined,
  devTenant: env.VITE_SLT_DEV_TENANT || undefined,
  devPrincipal: env.VITE_SLT_DEV_PRINCIPAL || undefined,
  dataSource: (env.VITE_DATA_SOURCE as AppConfig["dataSource"]) || "auto",
  mockFallback: flag(env.VITE_MOCK_FALLBACK, true),
  defaultSiteCode: env.VITE_DEFAULT_SITE || "OEMA",
  identity:
    env.VITE_IDP_API_KEY && env.VITE_IDP_AUTH_DOMAIN && env.VITE_IDP_PROJECT_ID
      ? { apiKey: env.VITE_IDP_API_KEY, authDomain: env.VITE_IDP_AUTH_DOMAIN, projectId: env.VITE_IDP_PROJECT_ID }
      : undefined,
};

/*
 * The connection, set from the Settings screen.
 *
 * The build bakes in a platform address, a credential and a default site;
 * an operator at a terminal cannot edit a build. What they can do is point
 * this copy at a platform from Settings, and that is kept here — in this
 * browser only, the same honesty Sky's keys have: it stops nobody who can
 * open the browser's storage, so it is for a development or review copy,
 * not for a credential that matters. An override applies on the next load,
 * because the data source is chosen once at start.
 */
export type ConnectionOverride = Partial<
  Pick<AppConfig, "apiUrl" | "apiKey" | "devTenant" | "devPrincipal" | "dataSource" | "defaultSiteCode">
>;

export const CONNECTION_KEY = "slt.connection";

export function loadConnectionOverride(): ConnectionOverride {
  try {
    const raw = localStorage.getItem(CONNECTION_KEY);
    if (!raw) return {};
    const v = JSON.parse(raw) as Record<string, unknown>;
    const str = (k: string) =>
      typeof v[k] === "string" && (v[k] as string).trim() ? (v[k] as string).trim() : undefined;
    const ds = str("dataSource");
    return {
      apiUrl: str("apiUrl"),
      apiKey: str("apiKey"),
      devTenant: str("devTenant"),
      devPrincipal: str("devPrincipal"),
      dataSource: ds === "platform" || ds === "mock" || ds === "auto" ? ds : undefined,
      defaultSiteCode: str("defaultSiteCode"),
    };
  } catch {
    return {};
  }
}

export function saveConnectionOverride(o: ConnectionOverride): void {
  try {
    const kept = Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== ""));
    if (Object.keys(kept).length === 0) localStorage.removeItem(CONNECTION_KEY);
    else localStorage.setItem(CONNECTION_KEY, JSON.stringify(kept));
  } catch {
    /* storage unavailable: the override lasts as long as this page */
  }
}

export function clearConnectionOverride(): void {
  try {
    localStorage.removeItem(CONNECTION_KEY);
  } catch {
    /* nothing to clear */
  }
}

function withOverride(base: AppConfig, o: ConnectionOverride): AppConfig {
  const next = { ...base };
  for (const k of ["apiUrl", "apiKey", "devTenant", "devPrincipal", "dataSource", "defaultSiteCode"] as const) {
    const v = o[k];
    if (v !== undefined) (next as Record<string, unknown>)[k] = v;
  }
  return next;
}

/** The build's settings with this browser's overrides on top. */
export const config: AppConfig = withOverride(builtConfig, loadConnectionOverride());

export function resolvedDataSource(c: AppConfig = config): DataSourceKind {
  if (c.dataSource === "auto") return c.apiUrl ? "platform" : "mock";
  return c.dataSource;
}
